// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { BURN_ADDRESS, PREAMBLE } from './constants.js';
import { Account } from './account.js';
import { bytes, dec, hex } from './convert.js';
import Ed25519 from './ed25519.js';
import { Node } from './node.js';
import Tools from './tools.js';
/**
* Represents a block as defined by the Nano cryptocurrency protocol. The Block
* class is abstract and cannot be directly instantiated. Every block must be one
* of three derived classes: SendBlock, ReceiveBlock, ChangeBlock.
*/
class Block {
    account;
    type = 'state';
    constructor(account) {
        if (this.constructor === Block) {
            throw new Error('Block is an abstract class and cannot be instantiated directly.');
        }
        if (account.constructor === Account) {
            this.account = account;
        }
        else if (typeof account === 'string') {
            this.account = new Account(account);
        }
        else {
            throw new TypeError('Invalid account');
        }
    }
    /**
    * Converts the block to JSON format as expected by the `process` RPC.
    *
    * @returns JSON representation of the block
    */
    json() {
        return {
            "type": this.type,
            "account": this.account.address,
            "previous": this.previous,
            "representative": this.representative.address,
            "balance": this.balance.toString(),
            "link": this.link,
            "signature": this.signature ?? '',
            "work": this.work ?? ''
        };
    }
    /**
    * Hashes the block using Blake2b.
    *
    * @returns {Promise<Uint8Array>} Block data hashed to a byte array
    */
    async hash() {
        const data = [
            PREAMBLE,
            this.account.publicKey,
            this.previous,
            this.representative.publicKey,
            dec.toHex(this.balance, 32),
            this.link
        ];
        return Tools.blake2b(data);
    }
    /**
    * Sends the block to a node for calculating proof-of-work on the network.
    *
    * A successful response sets the `work` property.
    *
    * @param {Node|string|URL} node - Node information required to call `work_generate`
    */
    async pow(node) {
        if (typeof node === 'string' || node.constructor === URL) {
            node = new Node(node);
        }
        if (node.constructor !== Node) {
            throw new TypeError('RPC must be a valid node');
        }
        const data = {
            "hash": this.previous
        };
        const { work } = await node.call('work_generate', data);
        this.work = work;
    }
    async sign(input, block) {
        if (typeof input === 'number') {
            const index = input;
            const { Ledger } = await import('./ledger.js');
            const ledger = await Ledger.init();
            await ledger.open();
            if (block) {
                try {
                    await ledger.updateCache(index, block);
                }
                catch (err) {
                    console.warn('Error updating Ledger cache of previous block, attempting signature anyway', err);
                }
            }
            const result = await ledger.sign(index, this);
            if (result.status !== 'OK') {
                throw new Error(result.status);
            }
            this.signature = result.signature;
        }
        else {
            const key = input ?? this.account.privateKey;
            if (!key) {
                throw new Error('No valid key found to sign block');
            }
            const signature = Ed25519.sign(await this.hash(), hex.toBytes(key));
            this.signature = bytes.toHex(signature);
        }
    }
    /**
    * Sends the block to a node for processing on the network.
    *
    * The block must already be signed (see `sign()` for more information).
    * The block must also have a `work` value.
    *
    * @param {Node|string|URL} node - Node information required to call `process`
    * @returns {Promise<string>} Hash of the processed block
    */
    async process(node) {
        if (!this.signature) {
            throw new Error('Block is missing signature. Use sign() and try again.');
        }
        if (!this.work == null) {
            throw new Error('Block is missing proof-of-work. Generate PoW and try again.');
        }
        const data = {
            "subtype": this.subtype,
            "json_block": "true",
            "block": this.json()
        };
        const res = await node.call('process', data);
        if (res.hash == null) {
            throw new Error('Block could not be processed', res);
        }
        return res.hash;
    }
    /**
    * Verifies the signature of the block. If a key is not provided, the public
    * key of the block's account will be used if it exists.
    *
    * @param {string} [key] - Hexadecimal-formatted public key to use for verification
    * @returns {boolean} True if block was signed by the matching private key
    */
    async verify(key) {
        key ??= this.account.publicKey;
        if (!key) {
            throw new Error('Provide a key for block signature verification.');
        }
        const data = [
            PREAMBLE,
            this.account.publicKey,
            this.previous,
            this.representative.publicKey,
            dec.toHex(this.balance, 32),
            this.link
        ];
        return Ed25519.verify(await Tools.blake2b(data), hex.toBytes(key), hex.toBytes(this.signature ?? ''));
    }
}
/**
* Represents a block that sends funds from one address to another as defined by
* the Nano cryptocurrency protocol.
*/
export class SendBlock extends Block {
    type = 'state';
    subtype = 'send';
    previous;
    representative;
    balance;
    link;
    signature;
    work;
    constructor(sender, balance, recipient, amount, representative, frontier, work) {
        super(sender);
        this.previous = frontier;
        this.representative = new Account(representative);
        this.link = new Account(recipient).publicKey;
        this.work = work ?? '';
        const bigBalance = BigInt(balance);
        const bigAmount = BigInt(amount);
        this.balance = bigBalance - bigAmount;
        validate(this);
    }
}
/**
* Represents a block that receives funds sent to one address from another as
* defined by the Nano cryptocurrency protocol.
*/
export class ReceiveBlock extends Block {
    type = 'state';
    subtype = 'receive';
    previous;
    representative;
    balance;
    link;
    signature;
    work;
    constructor(recipient, balance, origin, amount, representative, frontier, work) {
        super(recipient);
        this.previous = frontier ?? new Account(recipient).publicKey;
        this.representative = new Account(representative);
        this.link = origin;
        this.work = work ?? '';
        const bigBalance = BigInt(balance);
        const bigAmount = BigInt(amount);
        this.balance = bigBalance + bigAmount;
        validate(this);
    }
}
/**
* Represents a block that changes the representative account to which the user
* account delegates their vote weight using the the Open Representative Voting
* specification as defined by the Nano cryptocurrency protocol.
*/
export class ChangeBlock extends Block {
    type = 'state';
    subtype = 'change';
    previous;
    representative;
    balance;
    link = new Account(BURN_ADDRESS).publicKey;
    signature;
    work;
    constructor(account, balance, representative, frontier, work) {
        super(account);
        this.previous = frontier;
        this.representative = new Account(representative);
        this.balance = BigInt(balance);
        this.work = work ?? '';
        validate(this);
    }
}
/**
 * Validates block data.
 *
 * @param {Block} block - SendBlock, ReceiveBlock, or ChangeBlock to validate
 */
function validate(block) {
    if (block.account == null) {
        throw new Error('Account missing');
    }
    if (block.previous == null || block.previous === '') {
        throw new Error('Frontier missing');
    }
    if (block.representative == null) {
        throw new Error('Representative missing');
    }
    if (block.balance == null) {
        throw new Error('Balance missing');
    }
    if (block.balance < 0) {
        throw new Error('Negative balance');
    }
    switch (block.constructor) {
        case SendBlock: {
            if (block.link == null || block.link === '') {
                throw new Error('Recipient missing');
            }
            break;
        }
        case ReceiveBlock: {
            if (block.link == null) {
                throw new Error('Origin send block hash missing');
            }
            break;
        }
        case ChangeBlock: {
            if (block.link == null) {
                throw new Error('Change block link missing');
            }
            if (+block.link !== 0) {
                throw new Error('Invalid change block link');
            }
            break;
        }
    }
}
